// ---------------------------------------------------------------------------
// Time Machine 2025 — Exhibition page content
// ---------------------------------------------------------------------------
// All visible text on /paintings/timemachine-2025 lives here.
// Anything marked "PLACEHOLDER" is draft copy for SEO — edit freely.
// The page component (src/pages/paintings/TimeMachine2025.js) reads from
// this file; you don't need to touch the layout to update wording.
// ---------------------------------------------------------------------------

import cover from "../../images/Paintings/TimeMachine_2025/0_timeMachine2025.jpg";
import timeMachine from "../../images/Paintings/TimeMachine_2025/1_timeMachine.jpg";
import la5025 from "../../images/Paintings/TimeMachine_2025/2_la5025.JPG";
import la5025b from "../../images/Paintings/TimeMachine_2025/3_la5025.jpg";
import la5025c from "../../images/Paintings/TimeMachine_2025/4_la5025.jpg";
import la5025d from "../../images/Paintings/TimeMachine_2025/17_la5025.jpg";
import la5025e from "../../images/Paintings/TimeMachine_2025/18_la5025.jpg";
import millionYearsAgo5 from "../../images/Paintings/TimeMachine_2025/5_150millionYearsAgo.JPG";
import millionYearsAgo6 from "../../images/Paintings/TimeMachine_2025/6_150millionYearsAgo.JPG";
import millionYearsAgo7 from "../../images/Paintings/TimeMachine_2025/7_150millionYearsAgo.JPG";
import tokyo2174a from "../../images/Paintings/TimeMachine_2025/8_tokyo2174.jpg";
import tokyo2174b from "../../images/Paintings/TimeMachine_2025/9_tokyo2174.jpg";
import nighthawks10 from "../../images/Paintings/TimeMachine_2025/10_nighthawks.jpg";
import nighthawks11 from "../../images/Paintings/TimeMachine_2025/11_nighthawks.jpg";
import bondi1985a from "../../images/Paintings/TimeMachine_2025/12_bondi1985.jpg";
import bondi1985b from "../../images/Paintings/TimeMachine_2025/13_bondi1985.jpg";
import romanBathHouse from "../../images/Paintings/TimeMachine_2025/14_romanBathHouse.jpg";
import openingNight from "../../images/Paintings/TimeMachine_2025/15_openingNight.jpg";
import timeMachineInstall from "../../images/Paintings/TimeMachine_2025/19_timeMachineInstall.jpg";
import romanBathHouseInstall from "../../images/Paintings/TimeMachine_2025/20_romanBathHouseInstall.jpg";
import tokyo2174Install from "../../images/Paintings/TimeMachine_2025/21_tokyo2174Install.jpg";
import openingNight22 from "../../images/Paintings/TimeMachine_2025/22_openingNight.jpg";
import openingNight23 from "../../images/Paintings/TimeMachine_2025/23_openingNight.jpg";
import bondi1985Install from "../../images/Paintings/TimeMachine_2025/24_bondi1985.jpg";

// 800px sm versions for additionalImages thumbnails
import timeMachineInstallSm from "../../images/Paintings/TimeMachine_2025/sm/19_timeMachineInstall.jpg";
import la5025eSm           from "../../images/Paintings/TimeMachine_2025/sm/18_la5025.jpg";
import la5025dSm           from "../../images/Paintings/TimeMachine_2025/sm/17_la5025.jpg";
import la5025cSm           from "../../images/Paintings/TimeMachine_2025/sm/4_la5025.jpg";
import millionYearsAgo7Sm  from "../../images/Paintings/TimeMachine_2025/sm/7_150millionYearsAgo.jpg";
import millionYearsAgo5Sm  from "../../images/Paintings/TimeMachine_2025/sm/5_150millionYearsAgo.jpg";
import tokyo2174bSm        from "../../images/Paintings/TimeMachine_2025/sm/9_tokyo2174.jpg";
import nighthawks10Sm      from "../../images/Paintings/TimeMachine_2025/sm/10_nighthawks.jpg";
import bondi1985aSm        from "../../images/Paintings/TimeMachine_2025/sm/12_bondi1985.jpg";
import bondi1985InstallSm  from "../../images/Paintings/TimeMachine_2025/sm/24_bondi1985.jpg";
import romanBathHouseInstallSm from "../../images/Paintings/TimeMachine_2025/sm/20_romanBathHouseInstall.jpg";

// Build pre-filled mailto links for the three available works so the
// EnquireButton component can drop them straight into the page.
const enquiryLink = (workTitle, medium, dimensions) =>
  `mailto:tyrone@tyronelayne.com` +
  `?subject=${encodeURIComponent(`Enquiry — ${workTitle} (Time Machine, 2025)`)}` +
  `&body=${encodeURIComponent(
    `Hi Tyrone,\n\nI'm interested in ${workTitle} (${medium}, ${dimensions}) from the Time Machine exhibition.\n\nCould you tell me more about availability and shipping to my location?\n\nThanks`
  )}`;

const exhibition = {
  // -----------------------------------------------------------------
  // SEO + page metadata
  // -----------------------------------------------------------------
  seo: {
    pageTitle:
      "Time Machine 2025 — Solo Exhibition by Tyrone Layne | Surry Hills, Sydney",
    ogTitle: "Time Machine 2025 — Solo Exhibition by Tyrone Layne",
    metaDescription:
      "Time Machine — Tyrone Layne's 10th solo exhibition, 4 – 15 February 2025 at 50 Buckingham Street, Surry Hills, Sydney. Nine new oil paintings move between deep prehistory, mid-century New York, 1980s Bondi and a speculative future Los Angeles.",
    // Path the page is mounted at — used for canonical URL + JSON-LD url
    canonicalPath: "/paintings/timemachine-2025",
  },

  // -----------------------------------------------------------------
  // RUN DATES — ISO format, used in JSON-LD (ExhibitionEvent)
  // -----------------------------------------------------------------
  runDates: {
    start: "2025-02-04",
    end: "2025-02-15",
  },

  // -----------------------------------------------------------------
  // VENUE — used in JSON-LD (Place / PostalAddress)
  // -----------------------------------------------------------------
  venue: {
    name: "50 Buckingham Street",
    streetAddress: "50 Buckingham Street",
    addressLocality: "Surry Hills",
    addressRegion: "NSW",
    addressCountry: "AU",
  },

  // -----------------------------------------------------------------
  // HERO
  // -----------------------------------------------------------------
  hero: {
    title: "Time Machine",
    subtitle: "New Paintings by Tyrone Layne",
    dates: "4 – 15 February 2025",
    location: "50 Buckingham Street, Surry Hills, Sydney, Australia",
    coverImage: cover,
    coverAlt:
      "Time Machine — solo exhibition by Tyrone Layne, Surry Hills, 2025",
    // Backgrounds for the editorial quote + intro sections — separate
    // opening night photos so the top of the page doesn't repeat the
    // cover image three times.
    quoteImage: openingNight22,
    introImage: openingNight23,
  },

  // -----------------------------------------------------------------
  // ARTIST QUOTE (pull-quote near the top of the page)
  // -----------------------------------------------------------------
  artistQuote: {
    text:
      "Time Machine is a fictional series with a sci-fi theme. The collection follows a young woman and her German Shepherd as they embark through space and time — she has a chance encounter with a scientist at a bus stop on a rainy Sydney morning, he slips her a card, and it leads her on adventures beyond her imagination.",
    attribution: "Tyrone Layne",
  },

  // -----------------------------------------------------------------
  // INTRODUCTION (long-form prose under the quote)
  // -----------------------------------------------------------------
  intro: [
    "The Sydney-based artist is best known for his detailed, figurative works that capture people, places, time and culture. In this new collection of oil paintings, Layne brings his meticulous cityscapes into an imaginative new realm — mixed with humour and nostalgia.",
    "Each canvas is set in a specific place and moment: Los Angeles in the year 5025, a Roman bathhouse in the second century CE, a New York diner from 1942, an Australian beach in 1985.",
    "Time Machine is Layne's 10th solo exhibition and first in-person show in Sydney since 2017.",
  ],

  // -----------------------------------------------------------------
  // PAINTINGS — order matches the editorial flow of the page.
  //
  // Each entry:
  //   slug         unique id (used for React keys + JSON-LD)
  //   title        work title
  //   year         year painted
  //   medium       e.g. "Oil on linen"
  //   dimensions   e.g. "122 × 152 cm"
  //   installImage full-viewport install/contextual photo used as the
  //                STICKY BACKGROUND for this painting's section. The
  //                white card (with the painting + caption + commentary)
  //                scrolls UP over this image. Swap freely.
  //   installAlt   alt text for the install background
  //   mainImage    { src, alt } — the cleanest, single "painting alone"
  //                shot. Renders LARGE at the top of the white card.
  //   additionalImages  array of { src, alt, caption? } — alternate
  //                views, details and installation photos. Render as a
  //                grid BELOW the title/meta/Enquire button. May be
  //                empty.
  //   commentary   array of paragraphs (string[]) OR null for caption-only
  //   status       "available" | "sold" | "documentation"
  //   referenceNote (optional)  extra italicised line under the caption
  //                             (e.g. "After Edward Hopper, Nighthawks, 1942")
  // -----------------------------------------------------------------
  paintings: [
    {
      slug: "time-machine",
      title: "Time Machine",
      year: "2024",
      medium: "Oil on linen",
      dimensions: "25 × 25 cm",
      installImage: timeMachineInstall,
      installAlt:
        "Time Machine — installation view at TIME MACHINE, Surry Hills, 2025",
      mainImage: {
        src: timeMachine,
        alt: "Time Machine, oil on linen, 25 × 25 cm, Tyrone Layne, 2024",
      },
      additionalImages: [
        {
          src: timeMachineInstall,
          srcSm: timeMachineInstallSm,
          alt: "Time Machine — installation view, TIME MACHINE, Surry Hills, 2025",
          caption: "Installation view",
        },
      ],
      commentary: null,
      status: "sold",
    },
    {
      slug: "la5025",
      title: "Los Angeles 5025",
      year: "2024",
      medium: "Oil on linen",
      dimensions: "122 × 152 cm",
      installImage: la5025,
      installAlt:
        "Los Angeles 5025 — installation view at TIME MACHINE, Surry Hills, 2025",
      mainImage: {
        src: la5025b,
        alt: "Los Angeles 5025, oil on linen, 122 × 152 cm, Tyrone Layne, 2024",
      },
      additionalImages: [
        {
          src: la5025e,
          srcSm: la5025eSm,
          alt: "Los Angeles 5025 — alternate view",
        },
        {
          src: la5025d,
          srcSm: la5025dSm,
          alt: "Los Angeles 5025 — detail",
          caption: "Detail",
        },
        {
          src: la5025c,
          srcSm: la5025cSm,
          alt: "Los Angeles 5025 — detail",
          caption: "Detail",
        },
      ],
      commentary: [
        "Los Angeles 5025 imagines the city three thousand years from now, rendered as a single crowded block of Melrose Avenue in which the familiar and the unthinkable coexist without comment. Humans, aliens of several distinct species, cyborgs, and robots share the footpath in the unhurried way that Angelenos have always shared space — jostling for the same patch of sidewalk, ignoring one another with practiced ease. No hierarchy is established between them; they simply belong to the same city.",
        "The sky above is equally inhabited. UAPs drift at altitude alongside flying cars, and a robotic tree trimmer — blue, spindly, insistently municipal — tends to the street trees as though nothing has changed in the maintenance schedule. Hover cars line the kerb where sedans once parked. The palm trees remain. So do the Orthodox Jews, moving through the scene in black coats and wide brims, unchanged by three millennia in the way that only the genuinely committed are unchanged by anything. Cats claim the street as their own, threading between legs and loitering on warm concrete — the dominant animal of the strip, as they perhaps always were.",
        "The architecture reads as Melrose — low commercial frontages, the occasional newer tower pushing south against a denser skyline — but the skyline itself has thickened considerably, new high-rises crowding a horizon that still holds its smog-softened light. A gym called Pumping Iron anchors the right of the canvas, its signage a direct homage to the bodybuilding culture that made this part of the city famous and that apparently survives every civilisational upheaval intact. Beside it, the Jane Kordansky Gallery announces its lineage in its name alone — a descendant, presumably, of the current LA dealer David Kordansky, still operating in the same city thirty centuries on.",
        "The painting borrows the wide horizontal format of postwar American landscape painting, and Layne handles it with the eye of a production designer: the scene is lit for a film that hasn't been made, composed for a camera that doesn't yet exist, and packed with the kind of incidental detail that rewards the viewer who looks past the spectacle. Touches of 1990s nostalgia surface in the signage and street furniture — a reminder that Los Angeles has always recycled its own past even while selling the future.",
        "At 122 × 152 cm, it is one of the two largest canvases in the exhibition, and was conceived as a companion piece to 150 Million Years Ago, the show's other deep-time landscape. Where that work looks backward, Los Angeles 5025 looks forward by the same impossible distance. The two were hung in separate rooms at Buckingham Street, encountered in sequence as visitors moved through the show — each one a terminus, the present somewhere in between.",
      ],
      status: "available",
      mailto: enquiryLink("Los Angeles 5025", "Oil on linen", "122 × 152 cm"),
      // Optional secondary CTA — rendered as a second button below the
      // Enquire button. Use for paintings that also have a print edition.
      printLink: {
        href: "/prints/la5025",
        label: "Limited Edition Print Available",
      },
    },
    {
      slug: "150-million-years-ago",
      title: "150 Million Years Ago",
      year: "2024",
      medium: "Oil on linen",
      dimensions: "122 × 152 cm",
      installImage: millionYearsAgo5,
      installAlt:
        "150 Million Years Ago — installation view at TIME MACHINE, Surry Hills",
      mainImage: {
        src: millionYearsAgo6,
        alt: "150 Million Years Ago, oil on linen, 122 × 152 cm, Tyrone Layne, 2024",
      },
      additionalImages: [
        {
          src: millionYearsAgo7,
          srcSm: millionYearsAgo7Sm,
          alt: "150 Million Years Ago — detail",
          caption: "Detail",
        },
        {
          src: millionYearsAgo5,
          srcSm: millionYearsAgo5Sm,
          alt: "150 Million Years Ago — installation view at TIME MACHINE, Surry Hills",
          caption: "Installation view",
        },
      ],
      commentary: [
        "150 Million Years Ago drops the viewer into the Late Jurassic — a landscape painted not from fossils or museum reconstructions, but from the kind of half-remembered images that float around any childhood imagination of dinosaurs. The light is clear and the sky familiar, yet nothing else in the scene belongs to any world a living person has walked through.",
        "The time traveller and her German Shepherd move cautiously along a path in the foreground, the only figures who don't belong to this time. They press forward with the careful awareness of travellers who understand, without needing to be told, that the landscape around them operates by different rules. Behind them, Stegosauruses graze unhurriedly in the mid-ground, unaware — or indifferent — that they are being watched. In the shadowed bush at the back left, an Allosaurus peers through the dark foliage, its attention fixed on them. Along the right, Brachiosauruses move in the shallows of the river, their long necks sweeping down toward the water.",
        "The painting refuses the documentary tone usually attached to prehistory and treats the era instead as a place that could simply be visited — stumbled into, even, by the wrong species at the wrong moment.",
        "It was made as the deep-time counterweight to Los Angeles 5025 and shares its scale and ambition. The two were hung in separate rooms at Buckingham Street, encountered in sequence as visitors moved through the show — each one a terminus, the present somewhere in between.",
      ],
      status: "available",
      mailto: enquiryLink(
        "150 Million Years Ago",
        "Oil on linen",
        "122 × 152 cm"
      ),
    },
    {
      slug: "tokyo-2174",
      title: "Tokyo 2174",
      year: "2024",
      medium: "Oil on linen",
      dimensions: "51 × 61 cm",
      installImage: tokyo2174Install,
      installAlt:
        "Tokyo 2174 — installation view at TIME MACHINE, Surry Hills, 2025",
      mainImage: {
        src: tokyo2174a,
        alt: "Tokyo 2174, oil on linen, 51 × 61 cm, Tyrone Layne, 2024",
      },
      additionalImages: [
        {
          src: tokyo2174b,
          srcSm: tokyo2174bSm,
          alt: "Tokyo 2174 — detail",
          caption: "Detail",
        },
      ],
      commentary: [
        "Tokyo 2174 arrives at the exhibition's most sensory destination — a future Japan that has kept everything that makes Tokyo Tokyo and simply turned the volume up. The neon is brighter, the street is fuller, the signage more insistent, and the cherry blossom tree on the left blooms with the same stubborn grace it always has, indifferent to the robots sharing the footpath beneath it.",
        "The Hello Kitty billboard dominates the mid-left facade, marking 200 years of the character's cultural reign — apparently some brands are simply non-negotiable across centuries. McDonald's has also survived, its yellow archways a reliable beacon in any time zone, and E. Honda loiters outside it in a manner that suggests he has always been there and always will be. The Street Fighter aesthetic bleeds into the crowd more broadly: figures dressed for combat in a city that treats spectacle as civic infrastructure.",
        "The time traveller and her German Shepherd cross at the intersection on the right, moving through the scene with the practised calm of people who have already survived the Jurassic. By Tokyo 2174, a cyberpunk streetscape populated with robots, cosplayers, and two-hundred-year-old cartoon icons barely warrants a second glance.",
        "At 51 × 61 cm the painting punches well above its size — dense with incident, lit like a film set, and constructed with the same affection for urban chaos that runs through the rest of the series.",
      ],
      status: "sold",
    },
    {
      slug: "nighthawks-1942",
      title: "Nighthawks 1942 (after Edward Hopper)",
      year: "2025",
      medium: "Oil on linen",
      dimensions: "66 × 102 cm",
      installImage: nighthawks10,
      installAlt:
        "Nighthawks 1942 — installation view at TIME MACHINE, Surry Hills",
      mainImage: {
        src: nighthawks11,
        alt: "Nighthawks 1942 (after Edward Hopper), oil on linen, 66 × 102 cm, Tyrone Layne, 2025",
      },
      additionalImages: [
        {
          src: nighthawks10,
          srcSm: nighthawks10Sm,
          alt: "Nighthawks 1942 — installation view at TIME MACHINE, Surry Hills",
          caption: "Installation view",
        },
      ],
      referenceNote: "After Edward Hopper, Nighthawks, 1942",
      commentary: [
        "Nighthawks 1942 places Layne's time travellers inside one of the most recognisable images in American art. The woman and her German Shepherd are visible through the glass, moving along the sidewalk behind the counter — present in the scene but not of it, passing through 1942 the way travellers pass through any city they don't intend to stay in.",
        "Hopper's diner is otherwise faithfully reconstructed: the three patrons, the counter attendant, the Phillies sign, the late-night stillness that has made the original so endlessly reproduced. Layne doesn't disturb the composition so much as slip two figures into its margins, and in doing so quietly shifts what the painting is about. The isolation Hopper built so carefully is still there — but now there are witnesses to it, and they're only passing through.",
        "The date in the title is not incidental. 1942 is a destination, a coordinate the painting has been set to, and the figures outside the glass are the clearest sign that someone chose it deliberately.",
      ],
      status: "sold",
    },
    {
      slug: "bondi-1985",
      title: "Bondi 1985",
      year: "2025",
      medium: "Oil on linen",
      dimensions: "51 × 61 cm",
      installImage: bondi1985Install,
      installAlt:
        "Bondi 1985 — installation view at TIME MACHINE, Surry Hills, 2025",
      mainImage: {
        src: bondi1985b,
        alt: "Bondi 1985, oil on linen, 51 × 61 cm, Tyrone Layne, 2025",
      },
      additionalImages: [
        {
          src: bondi1985a,
          srcSm: bondi1985aSm,
          alt: "Bondi 1985 — alternate view",
        },
        {
          src: bondi1985Install,
          srcSm: bondi1985InstallSm,
          alt: "Bondi 1985 — installation view at TIME MACHINE, Surry Hills, 2025",
          caption: "Installation view",
        },
      ],
      commentary: [
        "Bondi 1985 places the time travellers on a towel in the middle of a busy summer afternoon — a pause in the journey rather than a destination with stakes. The woman and her German Shepherd have landed in a Bondi that neither of them belongs to, in a year that carries its own quiet significance: 1985 is the year Layne was born, and the figures sit among beachgoers whose age would have matched his parents' generation.",
        "The painting is composed from the colour world of mid-1980s Australian beach photography — chemical pinks, faded ultramarines, the slightly off-true skin tones of early Kodachrome — reconstructing a Sydney summer the artist knows only through cultural memory and the residue that era left on film.",
        "At 51 × 61 cm it is one of the smaller works in the show, but it earns its place among the more ambitious canvases. After the Jurassic and 1942 New York, there is something deliberately unhurried about two travellers simply lying in the sun — watching a crowd that has no idea what they've just come from, or where they're going next.",
      ],
      status: "sold",
    },
    {
      slug: "roman-bath-house",
      title:
        "Roman Bath House, 2nd Century CE (after Lawrence Alma-Tadema)",
      year: "2025",
      medium: "Oil on linen",
      dimensions: "36 × 41 cm",
      installImage: romanBathHouseInstall,
      installAlt:
        "Roman Bath House — installation view at TIME MACHINE, Surry Hills, 2025",
      mainImage: {
        src: romanBathHouse,
        alt: "Roman Bath House, 2nd Century CE (after Lawrence Alma-Tadema), oil on linen, 36 × 41 cm, Tyrone Layne, 2025",
      },
      additionalImages: [
        {
          src: romanBathHouseInstall,
          srcSm: romanBathHouseInstallSm,
          alt: "Roman Bath House — installation view at TIME MACHINE, Surry Hills, 2025",
          caption: "Installation view",
        },
      ],
      referenceNote:
        "After Lawrence Alma-Tadema, The Frigidarium",
      commentary: [
        "Roman Bath House is one of the smallest paintings in the exhibition and the one that most openly wears its influences. Layne works directly from Lawrence Alma-Tadema's The Frigidarium — the Victorian master's marble-white reconstruction of Imperial Rome — and inserts his time traveller into the scene with characteristic ease. She stands in the foreground in a dark gown, being dressed by a Roman attendant, her contemporary sneakers and clothes in a pile on the marble floor beside her. The dog, meanwhile, is being patted by the pool, entirely unbothered by the two thousand year displacement.",
        "Where the other paintings in the show place the travellers at the margins — observing, passing through — here she has gone native. The modern clothing discarded on the ground is the only evidence that she doesn't belong, and nobody in the room appears to have noticed.",
        "Alma-Tadema built his career on making ancient Rome feel inhabited and sensory rather than monumental. Layne borrows that quality and quietly updates it — the bathhouse remains opulent and unhurried, but it now contains a visitor who arrived from somewhere the Romans don't have a word for.",
      ],
      status: "sold",
    },
  ],

  // -----------------------------------------------------------------
  // CLOSING / installation documentation
  // -----------------------------------------------------------------
  closing: {
    image: openingNight,
    alt:
      "Opening night of TIME MACHINE by Tyrone Layne, 50 Buckingham St, Surry Hills, February 2025",
    caption: "Opening night",
    body:
      "Time Machine ran from 4 – 15 February 2025 at 50 Buckingham Street, Surry Hills — Tyrone Layne's 10th solo exhibition and his first in-person show in Sydney since 2017. The works pictured above remain in the artist's archive and private collections; three of the seven remain available — enquiries via the buttons throughout this page.",
  },
};

export default exhibition;
